import { recordMetrics, recordPeriods, recordSelection, renderLeaderboard } from './leaderboard-view.js';
import type { LeaderboardMetric, LeaderboardPeriod, LeaderboardSummary } from './types.js';

const initialized = new WeakSet<HTMLElement>();
export function initializeLeaderboardPage(root: HTMLElement, serverId: string,
  getLeaderboard: (serverId: string, period: LeaderboardPeriod) => Promise<LeaderboardSummary>, hydrate: () => void): void {
  if (initialized.has(root)) return;
  initialized.add(root);
  const result = root.querySelector<HTMLElement>('#leaderboardResult')!;
  const notice = root.querySelector<HTMLElement>('[data-record-notice]')!;
  const refresh = root.querySelector<HTMLButtonElement>('[data-record-refresh]')!;
  const params = new URLSearchParams(window.location.search);
  const requested = params.get('period') as LeaderboardPeriod;
  let period: LeaderboardPeriod = recordPeriods.includes(requested) ? requested : 'week';
  const state = recordSelection(params);
  let summary: LeaderboardSummary | undefined, sequence = 0, loading = false, lastRequested = period;

  function url(push = false): void {
    const next = new URL(window.location.href);
    next.searchParams.set('period', period); next.searchParams.set('metric', state.metric);
    if (state.query) next.searchParams.set('q', state.query); else next.searchParams.delete('q');
    if (state.scope === 'nearby') next.searchParams.set('view', 'nearby'); else next.searchParams.delete('view');
    if (push) window.history.pushState({}, '', next); else window.history.replaceState({}, '', next);
  }
  function periods(selected: LeaderboardPeriod): void {
    root.querySelectorAll<HTMLButtonElement>('[data-leaderboard-period]').forEach(button => {
      const active = button.dataset.leaderboardPeriod === selected;
      button.classList.toggle('is-active', active); button.setAttribute('aria-pressed', String(active));
    });
  }
  function render(): void {
    if (!summary || !root.isConnected) return;
    const focused = document.activeElement as HTMLInputElement | null;
    const searchFocused = focused?.matches('[data-record-search]');
    const caret = searchFocused ? focused?.selectionStart ?? null : null;
    result.innerHTML = renderLeaderboard(summary, state); hydrate();
    if (searchFocused) {
      const search = result.querySelector<HTMLInputElement>('[data-record-search]');
      search?.focus({ preventScroll: true });
      if (caret !== null) search?.setSelectionRange(caret, caret);
    }
  }
  async function load(next: LeaderboardPeriod): Promise<void> {
    const ticket = ++sequence; lastRequested = next; loading = true;
    result.setAttribute('aria-busy', 'true'); refresh.disabled = true; notice.hidden = true; periods(next);
    try {
      const data = await getLeaderboard(serverId, next);
      if (ticket !== sequence || !root.isConnected) return;
      summary = data; period = next; url(); render();
    } catch {
      if (ticket !== sequence || !root.isConnected) return;
      if (summary) { period = summary.period; periods(period); url(); }
      else result.innerHTML = '';
      notice.innerHTML = `<span>Could not refresh the standings.${summary ? ' Your last loaded results are still shown.' : ' Please try again.'}</span><button type='button' data-record-retry>Try again</button>`;
      notice.hidden = false;
    } finally {
      if (ticket === sequence && root.isConnected) { loading = false; result.setAttribute('aria-busy', 'false'); refresh.disabled = false; }
    }
  }
  root.addEventListener('click', event => {
    const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>('button') : null;
    if (!button || button.disabled) return;
    const nextPeriod = button.dataset.leaderboardPeriod as LeaderboardPeriod;
    const metric = button.dataset.recordMetric as LeaderboardMetric;
    if (recordPeriods.includes(nextPeriod)) { period = nextPeriod; state.page = 1; url(true); void load(nextPeriod); }
    else if (recordMetrics.includes(metric)) { state.metric = metric; state.page = 1; url(true); render(); }
    else if (button.hasAttribute('data-record-refresh')) void load(period);
    else if (button.hasAttribute('data-record-retry')) void load(lastRequested);
    else if (button.dataset.recordScope || button.hasAttribute('data-record-nearby')) {
      state.scope = button.dataset.recordScope === 'top' ? 'top' : 'nearby'; state.query = ''; state.page = 1; url(); render();
    }
    else if (button.dataset.recordPage) { state.page = Number(button.dataset.recordPage); render(); }
    else if (button.hasAttribute('data-record-clear-search')) { state.query = ''; state.page = 1; url(); render(); }
    else if (button.hasAttribute('data-record-find-me') && summary) {
      const entries = summary.boards.find(b => b.metric === state.metric)?.entries ?? [];
      const index = entries.findIndex(e => e.participantId === summary!.viewerParticipantId);
      const personal = summary.boards.find(b => b.metric === state.metric)?.personal;
      if (index < 0 && !personal) return;
      state.scope = index < 0 ? 'nearby' : 'top'; state.query = ''; state.page = index < 0 ? 1 : Math.floor(index / 20) + 1; url(); render();
      root.querySelector('[data-record-you]')?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
  });
  root.addEventListener('input', event => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-record-search]')) return;
    state.query = input.value.slice(0, 80); state.page = 1; url(); render();
  });
  const timer = window.setInterval(() => {
    if (!root.isConnected) { sequence++; window.clearInterval(timer); return; }
    if (!document.hidden && !loading && !root.querySelector('[data-record-search]:focus')) void load(period);
  }, 60_000);
  void load(period);
}
